import React from 'react';
import {
  GenerationJob,
  GenerationJobAction,
  GenerationJobConnectionState,
  GenerationJobPhase,
  GenerationJobStatus
} from '../types/generationJob';
import './GenerationJobPanel.css';

export interface GenerationJobPanelProps {
  job: GenerationJob;
  connectionState?: GenerationJobConnectionState;
  pollIntervalSeconds?: number;
  actionInProgress?: GenerationJobAction | null;
  disableActions?: boolean;
  onRetryFailed?: (pageIds: string[]) => void | Promise<void>;
  onPause?: () => void | Promise<void>;
  onResume?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  onOpenPage?: (pageId: string) => void;
}

const STATUS_LABELS: Record<GenerationJobStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  paused: 'Paused',
  failed: 'Failed',
  review_required: 'Review required',
  complete: 'Complete',
  cancelled: 'Cancelled'
};

const STATUS_DESCRIPTIONS: Record<GenerationJobStatus, string> = {
  queued: 'Waiting for an available generation slot. Your place in the queue is saved.',
  running: 'The server is generating this book. You can safely close this browser.',
  paused: 'Progress is saved. Resume when you are ready to continue.',
  failed: 'Generation stopped, but every successfully completed page was kept.',
  review_required: 'Some pages need attention, and every successfully completed page was kept.',
  complete: 'Every planned page finished successfully.',
  cancelled: 'Generation was cancelled. Pages completed before cancellation were kept.'
};

const PHASE_LABELS: Record<GenerationJobPhase, string> = {
  queued: 'Waiting in queue',
  planning: 'Preparing the book plan',
  'character-assets': 'Creating reusable characters',
  'background-assets': 'Creating reusable settings',
  'page-rendering': 'Generating story pages',
  validation: 'Checking generated pages',
  finalizing: 'Saving the finished draft',
  complete: 'Draft complete'
};

const STATUS_SYMBOLS: Record<GenerationJobStatus, string> = {
  queued: '…',
  running: '↻',
  paused: '‖',
  failed: '!',
  review_required: '△',
  complete: '✓',
  cancelled: '×'
};

const clampCounter = (value: number, total: number) => (
  Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), total)
);

const formatTimestamp = (value?: string) => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const connectionCopy = (
  state: GenerationJobConnectionState,
  pollIntervalSeconds: number
) => {
  switch (state) {
    case 'reconnecting':
      return 'Reconnecting… Showing the last saved progress while generation continues on the server.';
    case 'offline':
      return 'You appear to be offline. Showing the last saved progress; the server job may still be running.';
    case 'stopped':
      return 'Live progress checks have stopped.';
    case 'polling':
    default:
      return `Checking for saved progress every ${pollIntervalSeconds} seconds. You may close this page safely.`;
  }
};

const actionLabel = (action: GenerationJobAction, active?: GenerationJobAction | null) => {
  if (active !== action) {
    if (action === 'retry') return 'Retry failed pages';
    if (action === 'pause') return 'Pause generation';
    if (action === 'resume') return 'Resume generation';
    return 'Cancel generation';
  }

  if (action === 'retry') return 'Retrying…';
  if (action === 'pause') return 'Pausing…';
  if (action === 'resume') return 'Resuming…';
  return 'Cancelling…';
};

const GenerationJobPanel: React.FC<GenerationJobPanelProps> = ({
  job,
  connectionState = 'polling',
  pollIntervalSeconds = 5,
  actionInProgress,
  disableActions = false,
  onRetryFailed,
  onPause,
  onResume,
  onCancel,
  onOpenPage
}) => {
  const { counters } = job.progress;
  const total = Math.max(Number.isFinite(counters.total) ? counters.total : 0, 0);
  const complete = clampCounter(counters.complete, total);
  const failed = clampCounter(counters.failed, total);
  const reviewRequired = clampCounter(counters.reviewRequired, total);
  const processed = clampCounter(complete + failed + reviewRequired, total);
  const progressPercent = total > 0 ? Math.round((processed / total) * 100) : 0;
  const phaseLabel = job.progress.phaseLabel || PHASE_LABELS[job.progress.phase];
  const updatedAt = formatTimestamp(job.updatedAt);
  const retryableFailures = job.failures.filter(failure => failure.retryable);
  const canRetry = Boolean(onRetryFailed)
    && (job.status === 'failed' || job.status === 'review_required')
    && (retryableFailures.length > 0 || failed > 0);
  const canPause = job.status === 'running' && Boolean(onPause);
  const canResume = job.status === 'paused' && Boolean(onResume);
  const canCancel = ['queued', 'running', 'paused'].includes(job.status) && Boolean(onCancel);
  const actionsDisabled = disableActions || Boolean(actionInProgress);
  const connectionMessage = connectionCopy(connectionState, Math.max(1, pollIntervalSeconds));

  return (
    <section
      className={`generation-job-panel status-${job.status}`}
      data-testid="generation-job-panel"
      data-job-id={job.id}
      data-job-status={job.status}
      data-job-phase={job.progress.phase}
      aria-labelledby={`generation-job-title-${job.id}`}
      aria-describedby={`generation-job-description-${job.id}`}
    >
      <header className="generation-job-header">
        <div className="generation-job-heading">
          <span className="generation-job-symbol" aria-hidden="true">
            {STATUS_SYMBOLS[job.status]}
          </span>
          <div>
            <p className="generation-job-eyebrow">Full-book generation</p>
            <h3 id={`generation-job-title-${job.id}`}>{STATUS_LABELS[job.status]}</h3>
          </div>
        </div>
        <span
          className={`generation-job-status-badge ${job.status}`}
          data-testid="generation-job-status"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {STATUS_LABELS[job.status]}
        </span>
      </header>

      <p id={`generation-job-description-${job.id}`} className="generation-job-description">
        {STATUS_DESCRIPTIONS[job.status]}
      </p>

      <div className="generation-job-phase-row">
        <div>
          <span>Saved phase</span>
          <strong data-testid="generation-job-phase">{phaseLabel}</strong>
          {job.progress.message && <small>{job.progress.message}</small>}
        </div>
        {job.progress.currentPageNumber !== undefined && job.status === 'running' && (
          <span className="generation-job-current-page">
            Working on page {job.progress.currentPageNumber}
          </span>
        )}
      </div>

      <div className="generation-job-progress-block">
        <div className="generation-job-progress-label">
          <span>{processed} of {total} pages processed</span>
          <strong>{progressPercent}%</strong>
        </div>
        <progress
          data-testid="generation-job-progress"
          aria-label={`${processed} of ${total} pages processed`}
          value={processed}
          max={Math.max(total, 1)}
        />
      </div>

      <dl className="generation-job-counters" aria-label="Saved page counts">
        <div className="complete">
          <dt>Complete</dt>
          <dd data-testid="generation-job-complete-count">{complete}</dd>
        </div>
        <div className="active">
          <dt>Running</dt>
          <dd>{clampCounter(counters.running, total)}</dd>
        </div>
        <div className="queued">
          <dt>Queued</dt>
          <dd>{clampCounter(counters.queued, total)}</dd>
        </div>
        <div className="review">
          <dt>Review</dt>
          <dd>{reviewRequired}</dd>
        </div>
        <div className="failed">
          <dt>Failed</dt>
          <dd data-testid="generation-job-failed-count">{failed}</dd>
        </div>
      </dl>

      {job.failures.length > 0 && (
        <section className="generation-job-failures" aria-labelledby={`generation-job-failures-${job.id}`}>
          <div className="generation-job-failures-heading">
            <h4 id={`generation-job-failures-${job.id}`}>Pages needing attention</h4>
            <span>{job.failures.length}</span>
          </div>
          <ul>
            {job.failures.map((failure) => (
              <li
                key={failure.pageJobId}
                data-testid="generation-job-failure"
                data-page-id={failure.pageId}
                data-failure-status={failure.status}
              >
                <div>
                  <strong>{failure.pageTitle || `Page ${failure.pageNumber}`}</strong>
                  <p>{failure.reason}</p>
                  <small>
                    Attempt {failure.attempts}
                    {failure.code ? ` · ${failure.code}` : ''}
                    {' · '}
                    {failure.retryable ? 'Can retry' : 'Manual review needed'}
                  </small>
                </div>
                {onOpenPage && (
                  <button type="button" onClick={() => onOpenPage(failure.pageId)}>
                    Open page
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div
        className={`generation-job-connection ${connectionState}`}
        data-testid="generation-job-connection"
        data-connection-state={connectionState}
        role="status"
        aria-live="polite"
      >
        <span className="generation-job-connection-dot" aria-hidden="true" />
        <span>{connectionMessage}</span>
        {updatedAt && <time dateTime={job.updatedAt}>Last saved {updatedAt}</time>}
      </div>

      {(canRetry || canPause || canResume || canCancel) && (
        <footer className="generation-job-actions" aria-label="Generation job actions">
          {canRetry && (
            <button
              type="button"
              className="generation-job-button primary"
              data-testid="generation-job-retry"
              disabled={actionsDisabled}
              onClick={() => void onRetryFailed?.(retryableFailures.map(failure => failure.pageId))}
            >
              {actionLabel('retry', actionInProgress)}
              {actionInProgress !== 'retry' && retryableFailures.length > 0 ? ` (${retryableFailures.length})` : ''}
            </button>
          )}
          {canPause && (
            <button
              type="button"
              className="generation-job-button secondary"
              data-testid="generation-job-pause"
              disabled={actionsDisabled}
              onClick={() => void onPause?.()}
            >
              {actionLabel('pause', actionInProgress)}
            </button>
          )}
          {canResume && (
            <button
              type="button"
              className="generation-job-button primary"
              data-testid="generation-job-resume"
              disabled={actionsDisabled}
              onClick={() => void onResume?.()}
            >
              {actionLabel('resume', actionInProgress)}
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              className="generation-job-button danger"
              data-testid="generation-job-cancel"
              disabled={actionsDisabled}
              onClick={() => void onCancel?.()}
            >
              {actionLabel('cancel', actionInProgress)}
            </button>
          )}
        </footer>
      )}
    </section>
  );
};

export default GenerationJobPanel;
